// Prequalification funnel — SELLER track. Same architecture as the buyer
// page: sessionStorage-persisted state (§7 — kept post-finalize for the
// Result route; cleared only by "Start over"), client-side indicative math
// for the live meter (prequalSizing.ts; the server engine stays
// authoritative at finalize), a per-step AI guide (usePrequalGuide), and
// finalize as the LAST FORM STEP navigating to
// /portal/prequalify/seller/result. Chrome + layout follow the §4
// public-funnel design system.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { cn } from "@/design-system/lib/utils";
import { FormField, Input, Button, Textarea } from "@/design-system/components/ui";
import { PortalShell } from "../../components/PortalShell";
import {
  prequalificationApi,
  type PrequalFinalizeResult,
  type PrequalSellerAnswers,
  type PrequalSellerPath,
} from "../../api/prequalification";
import { computeSellerSizing, RECAP_MINIMUM_OWNERSHIP_YEARS } from "./prequalSizing";
import {
  PATH_RECAP_NOTICE,
  PATH_REQUIRED_ERROR,
  PATH_STEP_HINT,
  PATH_STEP_TITLE,
  PATH_SWITCH_NOTICE,
  RECAP_BUSINESS_HINT,
  RECAP_BUSINESS_NAME_HELPER,
  RECAP_COMPLETED_NOUN,
  RECAP_FUNNEL_TITLE,
  RECAP_GOAL_HELPER,
  RECAP_GOAL_QUESTION,
  RECAP_MANAGEMENT_OPTIONS,
  RECAP_MANAGEMENT_QUESTION,
  RECAP_NUMBERS_NOTE,
  RECAP_OWNER_RELIANCE_HINT,
  RECAP_PLAN_STEP_LABEL,
  RECAP_PLAN_TITLE,
  RECAP_PROGRAM_OVERLINE,
  RECAP_PROGRAM_TERMS_SUBHEAD,
  RECAP_YEARS_OWNED_HELPER,
  RECAP_YEARS_OWNED_LABEL,
  RECAP_YEARS_OWNED_REQUIRED_ERROR,
  RECAP_YEARS_OWNED_UNDER_MINIMUM_NOTE,
  RECAP_YEARS_OWNED_WHOLE_NUMBER_ERROR,
  SELLER_PATH_CARDS,
  SELLER_RECAP_KNOCKOUTS,
  sellerPathFromQuery,
} from "./recapContent";
import {
  ABSENCE_OPTIONS,
  BOOKS_OPTIONS,
  DOWN_PAYMENT_AMOUNT_HELPER,
  DOWN_PAYMENT_HELPER,
  FREE_CASH_FLOW_DEFINITION,
  DOWN_PAYMENT_QUESTION,
  OWNER_HOURS_BANDS,
  ASSIST_SAVE_NOTE,
  PROGRAM_TERMS_SUBHEAD,
  REVENUE_DRIVEN_BANDS,
  SELLER_KNOCKOUTS,
  TRANSITION_OPTIONS,
  YES_NO_OPTIONS,
} from "./prequalContent";
import type { PrequalFunnelMarketingCopy } from "../../api/prequalFunnelCopy";
import {
  ChoiceField,
  CurrencyField,
  FinalizeSection,
  formatMoneyTyping,
  FunnelProgress,
  GuidePanel,
  KnockoutChecklist,
  nullIfNotPositive,
  parseMoney,
  ProgramGlanceCard,
  QuestionCard,
  SizingMeter,
  type FunnelStep,
} from "./PrequalBits";
import { usePrequalGuide } from "./usePrequalGuide";
import { usePrequalInvite } from "./usePrequalInvite";
import { PrequalInviteExpiredCard } from "./PrequalInviteExpiredCard";
import {
  useFunnelCopy,
  usePartnerBrand,
  usePrequalBase,
  usePrequalPartnerSlug,
} from "./usePartnerBrand";
import { PartnerFallbackNotice, PartnerThemeProvider } from "./PartnerThemeProvider";
import { hasText } from "./blankCopy";
import { PrequalSaveProgressCard } from "./PrequalSaveProgressCard";
import type { PrequalDraftLoad, PrequalFunnelPageProps } from "./prequalDraftAdapter";
import { useAnonymousPrequalDraftAdapter } from "./useAnonymousPrequalDraftAdapter";
import { usePrequalDraftSession } from "./usePrequalDraftSession";
import {
  clearFunnelState,
  loadScopedFunnelState,
  PREQUAL_SELLER_STORAGE_KEY,
  saveFunnelState,
  type PrequalSessionScope,
} from "./prequalStorage";
import { usePrequalAttributionCapture } from "./prequalAttribution";

/** Exported for the step-count drift test: this array is the source of truth
 *  for the seller funnel's length, and the server's
 *  `PrequalificationFunnelSteps.SellerStepCount` mirrors it by hand. Nothing
 *  imports across the language boundary, so the two are kept honest by
 *  asserting the server's denominator against this length. */
export const STEPS: FunnelStep[] = [
  // Owner Recapitalization (spec pack F1, Q12): the path fork is its own first
  // step. Inserted IN FRONT, so every stored seller StepIndex moved one to the
  // right (migration 20405254000000_PrequalSellerPathStep).
  { key: "path", label: "Path" },
  { key: "business", label: "Business" },
  { key: "fit-check", label: "Fit check" },
  { key: "numbers", label: "Numbers" },
  { key: "owner-reliance", label: "Owner reliance" },
  { key: "records", label: "Records" },
  { key: "exit", label: "Exit" },
  { key: "finalize", label: "Finalize" },
];

/** The step array as the chosen path shows it: the recap arm names Step 7
 *  "Plan" (F2g). The KEYS never change by arm — `StepIndex` is positional and the
 *  two arms share one array (OP3). */
function stepsFor(recap: boolean): FunnelStep[] {
  return recap
    ? STEPS.map((step) => (step.key === "exit" ? { ...step, label: RECAP_PLAN_STEP_LABEL } : step))
    : STEPS;
}

/** The recap arm's whole-years answer: a whole number ≥ 0, or null when blank or
 *  not a whole number (the step's validator names which). */
function parseWholeYears(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

/**
 * Each step's heading and hint, built from the funnel's resolved marketing copy.
 * See `buyerStepMeta` — same shape, same reasoning, and the two runs are kept
 * separate on purpose: four of these strings match a buyer sibling today by
 * coincidence, and the two fit checks ask different questions.
 */
function sellerStepMeta(
  m: PrequalFunnelMarketingCopy,
  recap: boolean,
): Record<string, { title: string; hint?: string }> {
  return {
    // Step 1 · Path is Phoenix-LOCKED (F1): code constants, never a block.
    path: { title: PATH_STEP_TITLE, hint: PATH_STEP_HINT },
    business: { title: m.stepTitleSellerBusiness, hint: recap ? RECAP_BUSINESS_HINT : undefined },
    "fit-check": { title: m.stepTitleSellerFitCheck },
    numbers: { title: m.stepTitleSellerNumbers, hint: m.stepHintSellerNumbers },
    "owner-reliance": {
      title: m.stepTitleSellerOwnerReliance,
      hint: recap ? RECAP_OWNER_RELIANCE_HINT : undefined,
    },
    records: { title: m.stepTitleSellerRecords },
    exit: { title: recap ? RECAP_PLAN_TITLE : m.stepTitleSellerExit },
    finalize: { title: m.stepTitleSellerFinalize, hint: m.stepHintSellerFinalize },
  };
}

interface SellerForm {
  /** Step 1 · Path (F1). Null until chosen — required and deliberately
   *  un-defaulted, except where the visitor arrived asking for the recap path
   *  (landing Card 3 or `?path=recap`). */
  sellerPath: PrequalSellerPath | null;
  /** Recap arm (F2b): whole years under the current owners, as typed. */
  yearsOwnedByCurrentOwners: string;
  /** Recap arm (F2g): who runs the business after closing. */
  recapManagement: string | null;
  /** Recap arm (F2g): what the owners want the recapitalization to achieve. */
  recapGoal: string;
  firstName: string;
  lastName: string;
  phone: string;
  businessName: string;
  industry: string;
  location: string;
  yearsInOperation: string;
  knockouts: Record<string, string>;
  annualRevenue: string;
  annualCashFlow: string;
  askingPrice: string;
  /** The dollar down payment, as the grouped digit string a currency field
   *  holds. OPTIONAL — blank and zero are both legitimate answers. */
  downPaymentAmount: string;
  ownerHoursBand: string | null;
  absenceOutcome: string | null;
  revenuePersonallyDrivenBand: string | null;
  customerConcentration: string | null;
  booksQuality: string | null;
  revenueSpike: string | null;
  transitionWillingness: string | null;
  reasonForSelling: string;
  email: string;
}

const initialForm: SellerForm = {
  sellerPath: null,
  yearsOwnedByCurrentOwners: "",
  recapManagement: null,
  recapGoal: "",
  firstName: "",
  lastName: "",
  phone: "",
  businessName: "",
  industry: "",
  location: "",
  yearsInOperation: "",
  knockouts: {},
  annualRevenue: "",
  annualCashFlow: "",
  askingPrice: "",
  downPaymentAmount: "",
  ownerHoursBand: null,
  absenceOutcome: null,
  revenuePersonallyDrivenBand: null,
  customerConcentration: null,
  booksQuality: null,
  revenueSpike: null,
  transitionWillingness: null,
  reasonForSelling: "",
  email: "",
};

/** §2 — per-step PRIMARY-field predicates: the AI guide never fires for a
 *  step whose primary fields are still empty (no tips about empty fields). */
function stepPrimaryFilled(stepKey: string, form: SellerForm): boolean {
  switch (stepKey) {
    case "path":
      return form.sellerPath !== null;
    case "business":
      return form.businessName.trim() !== "";
    case "fit-check":
      return Object.keys(form.knockouts).length > 0;
    case "numbers":
      return (parseMoney(form.annualRevenue) ?? 0) > 0 || (parseMoney(form.annualCashFlow) ?? 0) > 0;
    case "owner-reliance":
      return (
        form.ownerHoursBand !== null ||
        form.absenceOutcome !== null ||
        form.revenuePersonallyDrivenBand !== null
      );
    case "records":
      return (
        form.customerConcentration !== null ||
        form.booksQuality !== null ||
        form.revenueSpike !== null
      );
    case "exit":
      return form.sellerPath === "OwnerRecapitalization"
        ? form.recapManagement !== null || form.recapGoal.trim() !== ""
        : form.transitionWillingness !== null || form.reasonForSelling.trim() !== "";
    default:
      // finalize — contact capture, no tip.
      return false;
  }
}

function clampStep(index: number): number {
  return Math.min(Math.max(0, Math.trunc(index)), STEPS.length - 1);
}

/** Dollars → the grouped digit string the currency fields hold. Zero becomes
 *  BLANK, not "0": the meter's readiness is keyed on the field being non-empty,
 *  and a resumed draft with no figure entered yet has not answered that. */
function moneyToField(amount: number | null | undefined): string {
  return amount && amount > 0 ? formatMoneyTyping(String(amount)) : "";
}

// 🔴 RETIRED in prequal-v4: bandToFraction and nearestBand. There is no band to
// convert and no chip to snap — one optional dollar field is the whole input.

/** Server answers → funnel form. `phone` is not answer content, is not on the
 *  draft, and comes back blank on another device — an optional field, not a
 *  loss worth a column. */
function hydrateSellerForm(current: SellerForm, loaded: PrequalDraftLoad): SellerForm {
  const a = loaded.answers as PrequalSellerAnswers;
  return {
    ...current,
    firstName: loaded.identity.firstName || current.firstName,
    lastName: loaded.identity.lastName || current.lastName,
    email: loaded.identity.email || current.email,
    businessName: a.businessName ?? "",
    industry: a.industry ?? "",
    location: a.location ?? "",
    yearsInOperation: a.yearsInOperation != null ? String(a.yearsInOperation) : "",
    knockouts: a.knockouts ?? {},
    annualRevenue: moneyToField(a.annualRevenue),
    annualCashFlow: moneyToField(a.annualCashFlow),
    askingPrice: moneyToField(a.askingPrice),
    downPaymentAmount: moneyToField(a.downPaymentAmount),
    ownerHoursBand: a.ownerHoursBand ?? null,
    absenceOutcome: a.absenceOutcome ?? null,
    revenuePersonallyDrivenBand: a.revenuePersonallyDrivenBand ?? null,
    customerConcentration: a.customerConcentration ?? null,
    booksQuality: a.booksQuality ?? null,
    revenueSpike: a.revenueSpike ?? null,
    transitionWillingness: a.transitionWillingness ?? null,
    reasonForSelling: a.reasonForSelling ?? "",
    // The fork itself is the dangerous omission (skill: hydrateBuyerForm): a
    // resumed draft that came back without its path would read as a sale. Every
    // pre-fork seller draft was preset to Sale by the re-map migration.
    sellerPath: a.sellerPath ?? null,
    yearsOwnedByCurrentOwners:
      a.yearsOwnedByCurrentOwners != null ? String(a.yearsOwnedByCurrentOwners) : "",
    recapManagement: a.recapManagement ?? null,
    recapGoal: a.recapGoal ?? "",
  };
}

/**
 * A sessionStorage blob written BEFORE the path step existed (a tab open across
 * the deploy) carries no `sellerPath` key at all and its step positions are the
 * seven-step funnel's. It is the session-storage twin of the draft re-map
 * migration: shift its positions one to the right and record the sale path it
 * was on, so it resumes on the screen it left with Step 1 shown done.
 */
function upgradePreForkBlob<T extends { form: Partial<SellerForm>; stepIndex: number; maxReached: number }>(
  restored: T | null,
): T | null {
  if (restored === null || "sellerPath" in restored.form) return restored;
  return {
    ...restored,
    form: { ...restored.form, sellerPath: "Sale" },
    stepIndex: restored.stepIndex + 1,
    maxReached: restored.maxReached + 1,
  };
}

export function SellerPrequalifyPage({
  adapter: injectedAdapter,
  finalizeSlot,
}: PrequalFunnelPageProps = {}) {
  usePrequalAttributionCapture("prequalify-seller");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Whatever the flow resolved: the route's `:slug` on the public funnel, the
  // DRAFT's site under an assisted surface. This page does not decide, and so
  // cannot be the surface that forgets.
  const { brand, loading: brandPending, notFound } = usePartnerBrand();
  // Server-published fine print, falling back to the compiled-in constants.
  const { administratorLine, indicativeLabel, marketing } = useFunnelCopy();
  const invite = usePrequalInvite();
  const base = usePrequalBase();
  const partnerSlug = usePrequalPartnerSlug();

  // WHOSE application this tab is holding. Captured ONCE, at mount, from the
  // two synchronous halves of the URL — the raw route slug (not the resolved
  // brand, which arrives asynchronously and would restore first and check
  // second) and the invite token. Frozen deliberately: if the address bar's
  // scope drifts under a live mount, the answers on screen still belong to the
  // context they were typed in, and the next mount is what re-decides.
  const [scope] = useState<PrequalSessionScope>(() => ({
    partnerSlug,
    inviteToken: invite.token,
  }));

  // §7 — restore any in-progress (or finalized) state from sessionStorage,
  // but ONLY this context's. A blob from another partner site or another
  // invite is somebody else's name, email and answers; it is removed rather
  // than skipped.
  const [restored] = useState(() =>
    upgradePreForkBlob(
      loadScopedFunnelState<Partial<SellerForm>>(PREQUAL_SELLER_STORAGE_KEY, scope),
    ),
  );
  // F0/F1: landing Card 3 and a `?path=recap` link arrive with the recap card
  // preselected. Read at PARSE time, not in a post-mount effect, and applied
  // only where no path has been chosen yet — a restored choice always wins, and
  // an unknown value is ignored (nothing selected).
  const [form, setForm] = useState<SellerForm>(() => {
    const base: SellerForm = restored ? { ...initialForm, ...restored.form } : initialForm;
    const preselected = sellerPathFromQuery(searchParams.get("path"));
    return base.sellerPath === null && preselected !== null
      ? { ...base, sellerPath: preselected }
      : base;
  });
  // F1: the one-line notice shown when a visitor who has answered later steps
  // comes back and switches path. In memory only.
  const [pathSwitched, setPathSwitched] = useState(false);
  const [stepIndex, setStepIndex] = useState(() => (restored ? clampStep(restored.stepIndex) : 0));
  const [maxReached, setMaxReached] = useState(() =>
    restored ? clampStep(restored.maxReached) : 0,
  );
  const [finalized, setFinalized] = useState<PrequalFinalizeResult | null>(
    () => restored?.finalized ?? null,
  );
  const [stepError, setStepError] = useState<string | null>(null);

  // A COMPLETED application is never silently reopened. Landing back on the
  // funnel root holding one is ambiguous — the same person coming back for
  // their documents, and the next client in the chair, arrive at the same URL
  // — so it asks instead of guessing. Dismissed in memory only: choosing to
  // adjust must not cost them the completed copy the Result route reads.
  const [completedGateDismissed, setCompletedGateDismissed] = useState(false);
  const completedOnMount = restored?.finalized ?? null;

  // The persistence seam. The anonymous adapter is built unconditionally
  // (hooks cannot be conditional) but is pure construction and fires nothing,
  // so it is inert whenever the assisted surface has injected its own.
  const anonymousAdapter = useAnonymousPrequalDraftAdapter("Seller");
  const adapter = injectedAdapter ?? anonymousAdapter;
  // Assisted = somebody is driving this on the applicant's behalf. It trims the
  // chrome (see PortalShell's `compact`) and moves the save note to step one.
  // The PUBLIC funnel is anonymous and every one of these gates is false there.
  const assisted = adapter.mode === "assisted";
  const draft = usePrequalDraftSession({
    adapter,
    hasLocalState: restored !== null,
    onLoaded: (loaded) => {
      setForm((f) => hydrateSellerForm(f, loaded));
      setStepIndex(clampStep(loaded.stepIndex));
      setMaxReached((m) => Math.max(m, clampStep(loaded.stepIndex)));
    },
  });

  // sessionStorage stays exactly what it was: the same-tab fallback that keeps
  // the funnel working when the draft calls fail, and the store the Result
  // route reads. The server draft is the cross-device copy, not a replacement.
  useEffect(() => {
    saveFunnelState(PREQUAL_SELLER_STORAGE_KEY, { form, stepIndex, maxReached, finalized }, scope);
  }, [form, stepIndex, maxReached, finalized, scope]);

  const recap = form.sellerPath === "OwnerRecapitalization";
  const steps = useMemo(() => stepsFor(recap), [recap]);

  const answers: PrequalSellerAnswers = useMemo(() => {
    const years = Number(form.yearsInOperation.trim());
    // 🔴 THE PATH GATE (F1/F2d/F2g). The form keeps typing across a switch, so the
    // memo is what keeps the other path's answers off the wire: on the recap arm
    // no asking price, a zero down payment and no exit answers; on the sale arm no
    // years-owned or Plan answers. The engine forces the down payment to zero on
    // the recap arm too (both tiers), so a stale value cannot move a price.
    const onRecap = form.sellerPath === "OwnerRecapitalization";
    return {
      sellerPath: form.sellerPath,
      yearsOwnedByCurrentOwners: onRecap ? parseWholeYears(form.yearsOwnedByCurrentOwners) : null,
      recapManagement: onRecap ? form.recapManagement : null,
      recapGoal: onRecap ? form.recapGoal.trim() || null : null,
      businessName: form.businessName.trim() || null,
      industry: form.industry.trim() || null,
      location: form.location.trim() || null,
      yearsInOperation:
        form.yearsInOperation.trim() && Number.isFinite(years) ? Math.trunc(years) : null,
      annualRevenue: parseMoney(form.annualRevenue) ?? 0,
      annualCashFlow: parseMoney(form.annualCashFlow) ?? 0,
      // 🔴 A ZERO ASKING PRICE IS SENT AS null, AND THE DOWN PAYMENT BELOW IS NOT.
      // The two look identical and mean opposite things. A seller may genuinely
      // want no cash at closing — the server's own contract calls that field
      // "OPTIONAL and legitimately ZERO" — so a zero down payment is an answer and
      // is sent verbatim. Nobody sells a business for nothing, so a zero asking
      // price is the same statement as leaving the field blank, which this step's
      // own label invites ("optional, leave blank for an estimate"). Typing 0 is
      // the most natural way for a seller to say it.
      //
      // Normalising HERE rather than at the input means one value feeds both
      // consumers: the wire payload AND computeSellerSizing below, which reads
      // `answers`. It also makes the stored record say what the seller meant,
      // instead of leaving every later reader to re-decide what a 0 was — and they
      // did not agree. The client sizing guards `> 0` and the server engine guards
      // `> 0`; the document service's seller deal shape used a bare `??`, which
      // coalesces on null and not on zero, so a typed 0 SHADOWED the computed
      // supportable price and took the seller deal sheet down with it.
      askingPrice: onRecap ? null : nullIfNotPositive(parseMoney(form.askingPrice)),
      ownerHoursBand: form.ownerHoursBand,
      absenceOutcome: form.absenceOutcome,
      revenuePersonallyDrivenBand: form.revenuePersonallyDrivenBand,
      customerConcentration: form.customerConcentration,
      booksQuality: form.booksQuality,
      revenueSpike: form.revenueSpike,
      downPaymentAmount: onRecap ? 0 : parseMoney(form.downPaymentAmount),
      transitionWillingness: onRecap ? null : form.transitionWillingness,
      reasonForSelling: onRecap ? null : form.reasonForSelling.trim() || null,
      knockouts: form.knockouts,
    };
  }, [form]);

  // The driving input for the sizing meter: annual cash flow.
  const ready = parseMoney(form.annualCashFlow) !== null && form.annualCashFlow.trim() !== "";

  // Live sizing — pure client-side indicative math on every answer change
  // (no network; the server engine recomputes authoritatively at finalize).
  const result = useMemo(() => (ready ? computeSellerSizing(answers) : null), [answers, ready]);

  // §3.2/§2 — per-step guide map (only the CURRENT step's tip renders),
  // entry-gated on the primary-field predicate, auto-refreshed on a
  // debounce after answer changes. Failure keeps the last good tip.
  const guide = usePrequalGuide({
    steps,
    stepIndex,
    stepReady: (key) => stepPrimaryFilled(key, form),
    fetchTip: (stepKey) => prequalificationApi.guide({ role: "Seller", seller: answers, stepKey }),
  });

  const set =
    <K extends keyof SellerForm>(key: K) =>
    (value: SellerForm[K]) => {
      guide.onAnswerChanged();
      setForm((f) => ({ ...f, [key]: value }));
    };

  // ── Down payment: two inputs, one answer ──────────────────────────────
  //
  // The MIDPOINT of the quoted note range is the only honest basis for
  // converting between the two: a percentage scales with the price and a
  // fixed dollar figure does not, so the two modes agree exactly here and
  // part at the range's ends. Mirrors the engine's noteMid — the client
  // result's conservativeNote/maxNote ARE the range's low and high notes.
  //
  // A note is sized purely by cash flow, so this never moves in response to
  // a down-payment edit and the two inputs cannot chase each other.
  //
  // prequal-v7 (M37): the figure a typed down payment is measured against is the
  // 20-YEAR Maximum Financed Amount — the headline schedule (D5) — not the middle
  // of a DSCR range, which no longer exists.
  const noteMid = useMemo(() => {
    const maximum20 = result?.maxFinanced20;
    return maximum20 != null && maximum20 > 0 ? maximum20 : null;
  }, [result]);

  function typeDownPaymentAmount(next: string) {
    guide.onAnswerChanged();
    setForm((f) => ({ ...f, downPaymentAmount: next }));
  }

  /** The share of price a typed figure works out to — DERIVED for display only.
   *  `price = note + down`, so the denominator is the note plus the figure
   *  itself, matching SellerProgramEconomics.DerivedDownPaymentPercent. */
  const solvedDownPaymentPct = useMemo(() => {
    const amount = parseMoney(form.downPaymentAmount);
    if (amount === null || amount <= 0 || noteMid === null) return null;
    return amount / (noteMid + amount);
  }, [form.downPaymentAmount, noteMid]);

  /** What the glance card calls the buyer down payment: the seller's own figure
   *  once typed; until then the card states the indicative 4–6% range note
   *  holders typically require, with the seller deciding as note holder
   *  (approval §2, M27). */
  const downPaymentGlanceLabel = parseMoney(form.downPaymentAmount)
    ? `$${form.downPaymentAmount}`
    : undefined;

  function goTo(index: number) {
    setStepError(null);
    setStepIndex(index);
    setMaxReached((m) => Math.max(m, index));
    guide.onStepEnter(STEPS[index].key);
  }

  /** F1 / OP22: choosing a path. Switching clears exactly the answers that apply
   *  only to the other path — on → recap the asking price, down payment,
   *  transition and reason-for-selling; on → sale the years owned and the Plan
   *  answers — so no stale answer reaches the engine or staff detail. */
  function choosePath(next: PrequalSellerPath) {
    setStepError(null);
    if (form.sellerPath === next) return;
    guide.onAnswerChanged();
    if (form.sellerPath !== null && maxReached > 0) setPathSwitched(true);
    setForm((f) => {
      if (f.sellerPath === next) return f;
      return next === "OwnerRecapitalization"
        ? {
            ...f,
            sellerPath: next,
            askingPrice: "",
            downPaymentAmount: "",
            transitionWillingness: null,
            reasonForSelling: "",
          }
        : {
            ...f,
            sellerPath: next,
            yearsOwnedByCurrentOwners: "",
            recapManagement: null,
            recapGoal: "",
          };
    });
  }

  const identity = {
    firstName: form.firstName,
    lastName: form.lastName,
    email: form.email,
  };

  function advance() {
    const validation = validateStep();
    if (validation) {
      setStepError(validation);
      return;
    }
    const next = stepIndex + 1;
    // The draft is created by the POST behind this action — never by the
    // landing GET, which every mail scanner and link-preview bot fetches
    // unattended the moment an invite is delivered. Fire-and-forget: a save
    // that fails must not hold up somebody's questionnaire.
    draft.persist({ stepIndex: next, answers, identity });
    goTo(next);
  }

  function validateStep(): string | null {
    switch (STEPS[stepIndex].key) {
      case "path":
        return form.sellerPath !== null ? null : PATH_REQUIRED_ERROR;
      case "business": {
        if (!form.businessName.trim()) return "The business name is required.";
        if (!recap) return null;
        // F2b: required on the recap arm, a whole number.
        if (form.yearsOwnedByCurrentOwners.trim() === "") return RECAP_YEARS_OWNED_REQUIRED_ERROR;
        return parseWholeYears(form.yearsOwnedByCurrentOwners) === null
          ? RECAP_YEARS_OWNED_WHOLE_NUMBER_ERROR
          : null;
      }
      case "numbers":
        return parseMoney(form.annualRevenue) !== null &&
          form.annualRevenue.trim() !== "" &&
          parseMoney(form.annualCashFlow) !== null &&
          form.annualCashFlow.trim() !== ""
          ? null
          : "Annual revenue and annual cash flow are required.";
      default:
        return null;
    }
  }

  function handleFinalized(res: PrequalFinalizeResult) {
    setFinalized(res);
    // Persist synchronously — the navigation unmounts this page before the
    // save effect could run.
    saveFunnelState(
      PREQUAL_SELLER_STORAGE_KEY,
      { form, stepIndex, maxReached, finalized: res },
      scope,
    );
    navigate(`${base}/seller/result`);
  }

  // §7 — the only way funnel state is cleared.
  function startOver() {
    clearFunnelState(PREQUAL_SELLER_STORAGE_KEY);
    setForm(initialForm);
    setStepIndex(0);
    setMaxReached(0);
    setFinalized(null);
    setStepError(null);
    setPathSwitched(false);
    setCompletedGateDismissed(true);
    guide.reset();
  }

  const stepKey = STEPS[stepIndex].key;
  // Built from the RESOLVED copy, not a module constant — so a published edit
  // changes the step headings without a deploy.
  const meta = sellerStepMeta(marketing, recap)[stepKey];
  // F2a: the recap frame (overline, H1, subhead) applies on every step AFTER
  // Path once the recap card is chosen; Step 1's frame is the funnel's own (F1).
  const recapFrame = recap && stepKey !== "path";
  const ownedYears = parseWholeYears(form.yearsOwnedByCurrentOwners);

  // Prefill contact from a LIVE invite. The invite named this person at send
  // time, so making them retype it is pure friction. Applied once, and only
  // into fields that are still blank — a restored session, or anything the
  // visitor has already typed, always wins over the prefill.
  const invitePrefilled = useRef(false);
  useEffect(() => {
    const live = invite.invite;
    if (invitePrefilled.current || live === null || !live.isLive) return;
    invitePrefilled.current = true;
    setForm((current) => ({
      ...current,
      firstName: current.firstName || (live.firstName ?? ""),
      lastName: current.lastName || (live.lastName ?? ""),
      email: current.email || (live.email ?? ""),
    }));
  }, [invite.invite]);

  // A dead invite link is a friendly dead end, never the funnel and never an
  // error page — somebody was invited here by a person they know, so the useful
  // thing is to name who to ask. Rendered only once the lookup has SETTLED
  // (isDead stays false while pending), so a slow resolve never flashes this.
  if (invite.isDead && invite.invite !== null) {
    return (
      <PartnerThemeProvider brand={brand}>
        <PortalShell pageTitle="Seller prequalification" wide publicFunnel partner={brand} brandPending={brandPending} compact={assisted}>
          {notFound && !assisted && <PartnerFallbackNotice />}
          <PrequalInviteExpiredCard invite={invite.invite} role="Seller" />
        </PortalShell>
      </PartnerThemeProvider>
    );
  }

  // The completed-application fork. Deliberately a full stop rather than a
  // banner over the form: the one thing that must not happen is somebody
  // typing into what turns out to be a finished application.
  if (completedOnMount !== null && !completedGateDismissed) {
    return (
      <PartnerThemeProvider brand={brand}>
        <PortalShell pageTitle="Seller prequalification" wide publicFunnel partner={brand} brandPending={brandPending} compact={assisted}>
          {notFound && !assisted && <PartnerFallbackNotice />}
          <CompletedApplicationCard
            role="Seller"
            finalized={completedOnMount}
            onView={() => navigate(`${base}/seller/result`)}
            onAdjust={() => setCompletedGateDismissed(true)}
            onStartNew={startOver}
            recap={completedOnMount.sellerPath === "OwnerRecapitalization"}
          />
        </PortalShell>
      </PartnerThemeProvider>
    );
  }

  return (
    <PartnerThemeProvider brand={brand}>
    <PortalShell pageTitle="Seller prequalification" wide publicFunnel partner={brand} brandPending={brandPending} compact={assisted}>
      {notFound && !assisted && <PartnerFallbackNotice />}
      {/* ASSISTED trims this to the title. The overline names the Phoenix
          PROGRAM and the subhead recites its terms — both are pitch, written
          for a stranger deciding whether to start. The operator has read them
          twenty times, and the applicant beside them was pitched in person. */}
      <div className="mb-space-4">
        {assisted || !hasText(recapFrame ? RECAP_PROGRAM_OVERLINE : marketing.programOverline) ? null : (
          <p
            className="text-label uppercase tracking-widest sm:tracking-[0.24em] text-gold-deep mt-0 mb-space-2"
            data-testid="prequal-program-overline"
          >
            {recapFrame ? RECAP_PROGRAM_OVERLINE : marketing.programOverline}
          </p>
        )}
        <h1
          className="text-h3 sm:text-h2 font-display uppercase tracking-wide text-ink m-0"
          data-testid="prequal-funnel-title"
        >
          {recapFrame ? RECAP_FUNNEL_TITLE : "Seller prequalification"}
        </h1>
        <div className="h-[3px] w-16 bg-gold mt-space-2" aria-hidden="true" />
        {assisted ? (
          // Step one only. "You can stop and come back" is worth knowing while
          // the operator is deciding whether to keep going; above every step it
          // is noise by the third, and on the finalize step it arrives after
          // the window in which it was useful.
          stepIndex === 0 && (
            <p
              className="text-small text-ink-mid mt-space-3 mb-0"
              data-testid="assist-save-note"
            >
              {ASSIST_SAVE_NOTE}
            </p>
          )
        ) : (
          <p className="text-small text-ink-mid mt-space-3 mb-0" data-testid="prequal-funnel-subhead">
            {recapFrame ? RECAP_PROGRAM_TERMS_SUBHEAD : PROGRAM_TERMS_SUBHEAD}
          </p>
        )}
      </div>

      <FunnelProgress
        steps={steps}
        currentIndex={stepIndex}
        maxReached={maxReached}
        onSelect={goTo}
      />
      <p className="text-right mt-space-1 mb-0">
        <button
          type="button"
          onClick={startOver}
          data-testid="prequal-start-over"
          className="bg-transparent border-0 p-0 text-caption text-ink-faint hover:text-gold-deep cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-sm"
        >
          Start over
        </button>
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_23.125rem] gap-space-5 mt-space-4">
        <div className="min-w-0 flex flex-col gap-space-4">
          <QuestionCard
            stepNumber={stepIndex + 1}
            section={steps[stepIndex].label}
            title={meta?.title ?? steps[stepIndex].label}
            hint={meta?.hint}
          >
            {stepKey === "path" && (
              <SellerPathCards
                value={form.sellerPath}
                onChange={choosePath}
                recapSelected={recap}
                switched={pathSwitched}
              />
            )}

            {stepKey === "business" && (
              <div className="flex flex-col gap-space-3">
                <FormField
                  label="Business name"
                  required
                  helperText={recap ? RECAP_BUSINESS_NAME_HELPER : undefined}
                >
                  <Input
                    value={form.businessName}
                    onChange={(e) => set("businessName")(e.target.value)}
                    data-testid="prequal-seller-business"
                  />
                </FormField>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-3">
                  <FormField label="Industry">
                    <Input
                      value={form.industry}
                      onChange={(e) => set("industry")(e.target.value)}
                    />
                  </FormField>
                  <FormField label="Location">
                    <Input
                      value={form.location}
                      onChange={(e) => set("location")(e.target.value)}
                      placeholder="City, ST"
                    />
                  </FormField>
                  <FormField label="Years in operation">
                    <Input
                      inputMode="numeric"
                      value={form.yearsInOperation}
                      onChange={(e) => set("yearsInOperation")(e.target.value)}
                    />
                  </FormField>
                </div>
                {/* F2b, recap only: its own full-width row below the trio — its
                    label is too long for a third of the row (OP2). Its own
                    answer field; Years in operation is a different fact. */}
                {recap && (
                  <div className="flex flex-col gap-space-2">
                    <FormField
                      label={RECAP_YEARS_OWNED_LABEL}
                      required
                      helperText={RECAP_YEARS_OWNED_HELPER}
                    >
                      <Input
                        inputMode="numeric"
                        value={form.yearsOwnedByCurrentOwners}
                        onChange={(e) => set("yearsOwnedByCurrentOwners")(e.target.value)}
                        data-testid="prequal-seller-years-owned"
                      />
                    </FormField>
                    {ownedYears !== null && ownedYears < RECAP_MINIMUM_OWNERSHIP_YEARS && (
                      <p
                        className="m-0 text-small text-ink bg-gold-tint border border-gold-soft rounded-md p-space-3"
                        data-testid="prequal-seller-years-owned-note"
                      >
                        {RECAP_YEARS_OWNED_UNDER_MINIMUM_NOTE}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {stepKey === "fit-check" && (
              <KnockoutChecklist
                questions={recap ? SELLER_RECAP_KNOCKOUTS : SELLER_KNOCKOUTS}
                answers={form.knockouts}
                onAnswer={(key, value) => {
                  guide.onAnswerChanged();
                  setForm((f) => ({ ...f, knockouts: { ...f.knockouts, [key]: value } }));
                }}
              />
            )}

            {stepKey === "numbers" && (
              <div className="flex flex-col gap-space-3">
                <CurrencyField
                  label="Annual revenue"
                  required
                  value={form.annualRevenue}
                  onChange={set("annualRevenue")}
                  testId="prequal-seller-revenue"
                />
                <CurrencyField
                  label="Annual free cash flow"
                  required
                  value={form.annualCashFlow}
                  onChange={set("annualCashFlow")}
                  helperText={FREE_CASH_FLOW_DEFINITION}
                  testId="prequal-seller-cashflow"
                />
                {recap ? (
                  // F2d (Q14): no asking price and no down payment on the recap
                  // arm — the note sits where the asking-price field was.
                  <p className="text-small text-ink-mid m-0" data-testid="prequal-recap-numbers-note">
                    {RECAP_NUMBERS_NOTE}
                  </p>
                ) : (
                  <>
                    <CurrencyField
                      label="Asking price — optional, leave blank for an estimate"
                      value={form.askingPrice}
                      onChange={set("askingPrice")}
                      helperText="If provided, we show how the amount financed at this price compares with what your cash flow supports."
                      testId="prequal-seller-asking"
                    />
                    <CurrencyField
                      label={DOWN_PAYMENT_QUESTION}
                      value={form.downPaymentAmount}
                      onChange={typeDownPaymentAmount}
                      helperText={
                        solvedDownPaymentPct !== null
                          ? `About ${Math.round(solvedDownPaymentPct * 100)}% of the price at the 20-year maximum. ${DOWN_PAYMENT_AMOUNT_HELPER} ${DOWN_PAYMENT_HELPER}`
                          : `${DOWN_PAYMENT_HELPER} ${DOWN_PAYMENT_AMOUNT_HELPER}`
                      }
                      testId="prequal-downpayment-amount"
                    />
                  </>
                )}
              </div>
            )}

            {stepKey === "owner-reliance" && (
              <div className="flex flex-col gap-space-3">
                <ChoiceField
                  label="How many hours a week do you personally work in the business?"
                  options={OWNER_HOURS_BANDS}
                  value={form.ownerHoursBand}
                  onChange={set("ownerHoursBand")}
                  onAdvance={advance}
                  testIdPrefix="prequal-hours"
                />
                <ChoiceField
                  label="If you stepped away for 30 days, what would happen?"
                  options={ABSENCE_OPTIONS}
                  value={form.absenceOutcome}
                  onChange={set("absenceOutcome")}
                  onAdvance={advance}
                  testIdPrefix="prequal-absence"
                />
                <ChoiceField
                  label="Roughly what share of revenue do you personally drive?"
                  options={REVENUE_DRIVEN_BANDS}
                  value={form.revenuePersonallyDrivenBand}
                  onChange={set("revenuePersonallyDrivenBand")}
                  onAdvance={advance}
                  testIdPrefix="prequal-driven"
                />
              </div>
            )}

            {stepKey === "records" && (
              <div className="flex flex-col gap-space-3">
                <ChoiceField
                  label="Does any single customer account for more than 30% of revenue?"
                  options={YES_NO_OPTIONS}
                  value={form.customerConcentration}
                  onChange={set("customerConcentration")}
                  onAdvance={advance}
                  testIdPrefix="prequal-concentration"
                />
                <ChoiceField
                  label="How are the books kept?"
                  options={BOOKS_OPTIONS}
                  value={form.booksQuality}
                  onChange={set("booksQuality")}
                  onAdvance={advance}
                  testIdPrefix="prequal-books"
                />
                <ChoiceField
                  label="Has revenue spiked more than 15% recently?"
                  options={YES_NO_OPTIONS}
                  value={form.revenueSpike}
                  onChange={set("revenueSpike")}
                  onAdvance={advance}
                  testIdPrefix="prequal-spike"
                />
              </div>
            )}

            {stepKey === "exit" && recap && (
              // F2g: Step 7 · Plan — adapted, not skipped (OP3). Writes
              // recapManagement / recapGoal and never the exit answers.
              <div className="flex flex-col gap-space-3">
                <ChoiceField
                  label={RECAP_MANAGEMENT_QUESTION}
                  options={RECAP_MANAGEMENT_OPTIONS}
                  value={form.recapManagement}
                  onChange={set("recapManagement")}
                  onAdvance={advance}
                  testIdPrefix="prequal-recap-management"
                />
                <FormField label={RECAP_GOAL_QUESTION} helperText={RECAP_GOAL_HELPER}>
                  <Textarea
                    rows={3}
                    value={form.recapGoal}
                    onChange={(e) => set("recapGoal")(e.target.value)}
                    data-testid="prequal-recap-goal"
                  />
                </FormField>
              </div>
            )}

            {stepKey === "exit" && !recap && (
              <div className="flex flex-col gap-space-3">
                <ChoiceField
                  label="Are you willing to help the buyer transition?"
                  options={TRANSITION_OPTIONS}
                  value={form.transitionWillingness}
                  onChange={set("transitionWillingness")}
                  onAdvance={advance}
                  testIdPrefix="prequal-transition"
                />
                <FormField label="Why are you selling?">
                  <Textarea
                    rows={3}
                    value={form.reasonForSelling}
                    onChange={(e) => set("reasonForSelling")(e.target.value)}
                  />
                </FormField>
              </div>
            )}

            {stepKey === "finalize" && (
              <FinalizeSection
                role="Seller"
                sellerRecap={recap}
                result={result}
                contact={{
                  firstName: form.firstName,
                  lastName: form.lastName,
                  email: form.email,
                  phone: form.phone,
                }}
                onContactChange={(patch) => {
                  guide.onAnswerChanged();
                  setForm((f) => ({ ...f, ...patch }));
                }}
                buildRequest={(extra) => ({
                  role: "Seller",
                  seller: answers,
                  firstName: form.firstName.trim(),
                  lastName: form.lastName.trim(),
                  email: form.email.trim(),
                  phone: form.phone.trim() || undefined,
                  consentAccepted: extra.consentAccepted,
                  website: extra.website,
                  // The ROUTE slug, never `brand?.slug`: a buyer team's site
                  // resolves to NO brand (it renders natively — D2a), and the
                  // server resolves the slug itself, degrading an unknown one
                  // to the native funnel exactly as the draft path already
                  // does. Sending the resolved brand's slug dropped every
                  // team-site completion's attribution (review F1).
                  partnerSlug: partnerSlug ?? undefined,
                  inviteToken: invite.token ?? undefined,
                })}
                // Routed through the adapter so a draft is CONSUMED by the
                // submission it produced, in the same transaction. Submitting
                // around it would leave the finished applicant sitting on
                // somebody's abandoned-draft follow-up list.
                submit={draft.finalize}
                onFinalized={handleFinalized}
                beforeSubmit={finalizeSlot}
              />
            )}
          </QuestionCard>

          {stepError && (
            <p className="text-small text-status-error m-0" role="alert">
              {stepError}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-space-3">
            {stepIndex > 0 && (
              <Button type="button" variant="secondary" onClick={() => goTo(stepIndex - 1)}>
                ← Back
              </Button>
            )}
            {stepKey !== "finalize" && (
              <Button
                type="button"
                onClick={advance}
                className="hover:bg-gold-deep hover:text-white-pure"
                data-testid="prequal-next"
              >
                Continue →
              </Button>
            )}
          </div>
        </div>

        <aside className="flex flex-col gap-space-4">
          <SizingMeter role="Seller" result={result} ready={ready} sellerRecap={recap} />
          <PrequalSaveProgressCard
            mode={adapter.mode}
            invited={invite.token !== null}
            showIdentity={stepKey !== "finalize"}
            identity={identity}
            onIdentityChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            onSave={() => draft.persist({ stepIndex, answers, identity })}
            session={draft}
          />
          <ProgramGlanceCard
            role="Seller"
            sellerDownPaymentLabel={downPaymentGlanceLabel}
            sellerRecap={recap}
          />
          <GuidePanel
            text={guide.guides[stepKey] ?? null}
            onRefresh={guide.refreshNow}
            refreshing={guide.refreshing}
          />
          {hasText(administratorLine) && (
            <p className="text-caption text-ink-faint m-0" data-testid="prequal-administrator-line">{administratorLine}</p>
          )}
          {hasText(indicativeLabel) && (
            <p className="text-caption text-ink-faint m-0" data-testid="prequal-indicative-label">{indicativeLabel}</p>
          )}
        </aside>
      </div>
    </PortalShell>
    </PartnerThemeProvider>
  );
}

/**
 * What the funnel root shows when this browser is holding a COMPLETED
 * application for this exact context.
 *
 * Three named ways out, because three genuinely different people arrive here:
 * the applicant coming back for their documents, the applicant who spotted a
 * mistake, and the broker whose next client just sat down. Guessing between
 * them either hides somebody's results or shows a stranger's — so it asks.
 *
 * Nothing here destroys anything on its own: only the explicitly-labelled
 * "Start a new application" clears the stored answers, and the Result route
 * keeps reading them until it does.
 *
 * DUPLICATED VERBATIM in BuyerPrequalifyPage. The two funnel pages are
 * deliberate mirrors and their one shared home, PrequalBits, is owned
 * elsewhere in this change — keep the two copies in lockstep.
 */
function CompletedApplicationCard({
  role,
  finalized,
  onView,
  onAdjust,
  onStartNew,
  recap = false,
}: {
  role: "Buyer" | "Seller";
  finalized: PrequalFinalizeResult;
  onView: () => void;
  onAdjust: () => void;
  onStartNew: () => void;
  /** F3d: the recap arm names the application an owner recapitalization. */
  recap?: boolean;
}) {
  const reference = finalized.referenceNumber?.trim();
  // A soft decline's result route shows the warm thank-you rather than a
  // documents view, so the button must not promise one. (The premise used to be
  // that a decline HAS no documents. It has two now — they are emailed, and the
  // result screen says so — but there is still nothing here to go and look at.)
  const viewLabel =
    finalized.result?.outcome === "SoftDecline" ? "View my result" : "View my documents";

  return (
    <section
      className="rounded-[14px] border border-line bg-white-pure shadow-[0_10px_34px_rgba(20,20,20,0.07)] p-space-4 sm:p-space-6 max-w-2xl mx-auto"
      data-testid="prequal-completed-gate"
      aria-live="polite"
    >
      <p className="text-label uppercase tracking-widest sm:tracking-[0.24em] text-gold-deep mt-0 mb-space-2">
        Already submitted
      </p>
      <h1 className="font-semibold normal-case tracking-normal text-[1.45rem] leading-tight text-ink mt-0 mb-space-3">
        You’ve already finished this application
      </h1>
      <p className="text-small text-ink-mid m-0">
        This browser is still holding a completed{" "}
        {role === "Buyer" ? "buyer" : recap ? RECAP_COMPLETED_NOUN : "seller"} prequalification
        {reference ? (
          <>
            , reference <strong className="text-ink">{reference}</strong>
          </>
        ) : null}
        . Nothing has been lost — choose what you’d like to do.
      </p>

      <div className="flex flex-wrap items-center gap-space-3 mt-space-4">
        <Button type="button" onClick={onView} data-testid="prequal-completed-view">
          {viewLabel}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onStartNew}
          data-testid="prequal-completed-new"
        >
          Start a new application
        </Button>
      </div>
      <p className="text-caption text-ink-faint mt-space-2 mb-0">
        Starting a new application clears these answers from this browser. If somebody else is
        applying, that’s the one to choose.
      </p>

      <p className="text-small text-ink-mid mt-space-4 mb-0">
        Or{" "}
        <button
          type="button"
          onClick={onAdjust}
          data-testid="prequal-completed-adjust"
          className="bg-transparent border-0 p-0 text-gold-deep underline cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-sm"
        >
          adjust my answers
        </button>
        {" "}— reopen this application and change it.
      </p>
    </section>
  );
}

/**
 * Step 1 · Path (spec pack F1) — the seller funnel's fork between selling and
 * staying (Owner Recapitalization Loan).
 *
 * Follows the buyer `DocumentPathCards` pattern: a `role="group"` named by the
 * question line, cards with label / body / produces and `aria-pressed`; one
 * column under 640px, two from 640px. PHOENIX-LOCKED copy (OP7): code constants,
 * never Document Language blocks. Required and un-defaulted — except that a
 * visitor who arrived asking for the recap path (landing Card 3, `?path=recap`)
 * finds Card B already selected and can change it.
 */
function SellerPathCards({
  value,
  onChange,
  recapSelected,
  switched,
}: {
  value: PrequalSellerPath | null;
  onChange: (value: PrequalSellerPath) => void;
  recapSelected: boolean;
  /** The visitor came back and switched after answering later steps (F1). */
  switched: boolean;
}) {
  const headingId = useId();
  return (
    <div className="flex flex-col gap-space-3">
      <div
        role="group"
        aria-labelledby={headingId}
        className="grid grid-cols-1 min-[640px]:grid-cols-2 gap-space-4"
        data-testid="prequal-seller-path-cards"
      >
        <span id={headingId} className="sr-only">
          {PATH_STEP_TITLE}
        </span>
        {SELLER_PATH_CARDS.map((card) => {
          const selected = value === card.value;
          return (
            <button
              key={card.value}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(card.value)}
              data-testid={`prequal-seller-path-${card.value}`}
              className={cn(
                "text-left rounded-md border p-space-3 transition-colors duration-fast ease-standard",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-1",
                selected
                  ? "border-gold bg-gold-tint shadow-[inset_0_0_0_1px_var(--color-gold)]"
                  : "border-line bg-white-pure hover:border-gold hover:bg-gold-tint",
              )}
            >
              <span className="block text-small font-semibold text-ink">{card.label}</span>
              <span className="block text-small text-ink-mid mt-space-1">{card.body}</span>
              <span className="block text-caption text-ink-faint mt-space-2">{card.produces}</span>
            </button>
          );
        })}
      </div>
      {recapSelected && (
        <p
          className="m-0 text-small text-ink bg-gold-tint border border-gold-soft rounded-md p-space-3"
          data-testid="prequal-seller-path-recap-notice"
        >
          {PATH_RECAP_NOTICE}
        </p>
      )}
      {switched && (
        <p className="m-0 text-caption text-ink-mid" data-testid="prequal-seller-path-switch-notice">
          {PATH_SWITCH_NOTICE}
        </p>
      )}
    </div>
  );
}
